import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Badge, Button, EditIcon, LoadingSection, showToast } from '@quickmd-llc/design-system'
import type { ServiceTypeAdminApiInContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { Breadcrumb } from '@/components/breadcrumb/Breadcrumb'
import type { Crumb } from '@/components/breadcrumb/Breadcrumb'
import { EditPageShell } from '@/components/page-shell/EditPageShell'
import { ServiceInfoSection } from './components/ServiceInfoSection/ServiceInfoSection'
import { VisitTypesSection } from './components/VisitTypesSection/VisitTypesSection'
import { ViewServiceFooter } from './components/ViewServiceFooter/ViewServiceFooter'
import { DeleteServiceTypePopup } from './components/DeleteServiceTypePopup/DeleteServiceTypePopup'
import { DeleteVisitTypePopup } from './components/DeleteVisitTypePopup/DeleteVisitTypePopup'
import type { VisitTypeFormState } from './components/visit-type-form/VisitTypeForm'
import type { ConsentFormEntry, ViewServiceVisitType } from './types'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import { formatMoney, hasOwnProperty, isObjectDeeplyEqual, toFindDuplicates } from '../../utils/common'
import { changeTabs } from '../../store/management/ManagementActions'
import { useService } from './hooks/useService'
import { useSaveService } from './hooks/useSaveService'
import { useDeleteServiceType } from './hooks/useDeleteServiceType'
import { useIntakeFormTemplates } from '../../hooks/useIntakeFormTemplates'
import { useConsentForms } from '../../hooks/useConsentForms'
import { MAX_VISIT_PRICE, QUICK_MD_TITLE_TEXT } from '../../utils/constants'
import {
  checkInvalidCombination,
  checkIsVisibilityTypeAllOrNew,
} from '../new-service/helpers/checkIsVisibilityTypeAllOrNew'
import { normalizeIntakeForms, readOptionalProp, readStringProp } from '@/utils/narrowing'
import { DiscardChangesPopup } from '../../components/discard-changes-popup/DiscardChangesPopup'
import { useDiscardChangesGuard } from '../../components/discard-changes-popup/useDiscardChangesGuard'

const maxServiceNameLength = 70
const maxDescriptionLength = 500
const maxServiceAbbreviationLength = 4

function readIsActive(obj: object): boolean {
  // Original: `savedData[id].IsActive !== false` — undefined counts as active.
  return readOptionalProp(obj, 'IsActive') !== false
}

function readExcludedStateCodes(obj: object): string[] | undefined {
  const value = readOptionalProp(obj, 'ExcludedStateCodes')
  return Array.isArray(value) && value.every((s): s is string => typeof s === 'string') ? value : undefined
}

const emptyVisitTypeErrors = {
  visitLengthError: '',
  visitLengthLocalError: '',
  coastUSDError: '',
  coastUSDLocalError: '',
  cancellationFeeError: '',
  cancellationFeeLocalError: '',
  visitNameError: '',
  visitNameLocalError: '',
  visitDescriptionError: '',
  visitDescriptionLocalError: '',
  consentFormError: '',
  consentFormLocalError: '',
  visibilityTypeError: '',
} as const

/** The raw save payload built from the working set (nullable, mirrors the legacy JS object). */
type RawServiceVisitTypePayload = {
  IntakeFormId: string | null | undefined
  ConsentForms: Array<{ ConsentFormId: string | null | undefined }>
  VisitTypeId: string | null
  Name: string | null
  IsMedicationAssistedTreatment: boolean
  IsPostBookingReminderRequired: boolean
  IsNewPatientSpacingEnabled: boolean
  Duration: number
  Description: string | null
  Cost: number
  CancellationFee: number
  VisibilityType: string | null
  IsPhoneVisitCommunicationTypeAllowed: boolean
  IsInPerson: boolean
  IsAsynchronous: boolean
}

type RawServicePayload = {
  Name: string
  Description: string
  Abbreviation: string
  ServiceTypeId: string | undefined
  ExcludedStateCodes: string[]
  VisitTypes: RawServiceVisitTypePayload[]
}

/** Per-visit-type error accumulator used while validating the working set. */
type LocalErrors = {
  id: string | null
  errors: {
    visitLengthError: string | boolean
    visitLengthLocalError: string
    coastUSDError: string | boolean
    coastUSDLocalError: string
    cancellationFeeError: string | boolean
    cancellationFeeLocalError: string
    visitNameError: string | boolean
    visitNameLocalError: string
    intakeFormError: string | boolean
    intakeFormLocalError: string
    visitDescriptionError: string | boolean
    visitDescriptionLocalError: string
    consentFormError: string | boolean
    consentFormLocalError: string
    visibilityTypeError: string
  }
}

export default function ViewService() {
  const params = new URLSearchParams(window.location.search)
  const navigate = useNavigate()
  const id = params.get('id')
  const { state: locationState } = useLocation()
  const serverImageUrlRef = useRef('')
  const imageFileRef = useRef<File | null>(null)

  const [saveSuccess, setSaveSuccess] = useState(false)
  const { intakeForms } = useIntakeFormTemplates()
  const [error, setError] = useState('')
  const { savedData, isLoading: isServiceLoading, errorMessage: serviceErrorMessage } = useService(id)
  const { saveService, isPending: isSavePending } = useSaveService()
  const { deleteServiceType, isPending: isDeletePending } = useDeleteServiceType()
  const { additionalConsentFormsOptions } = useConsentForms()

  const loading = isServiceLoading || isSavePending || isDeletePending

  const [serviceName, setServiceName] = useState('')
  const [serviceNameError, setServiceNameError] = useState('')
  const [description, setDescription] = useState('')
  const [descriptionLocalError, setDescriptionLocalError] = useState('')
  const [serviceTypes, setServiceTypes] = useState<ViewServiceVisitType[]>([])
  const [isDescriptionError, setIsDescriptionError] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [serviceAbbreviation, setServiceAbbreviation] = useState('')
  const [serviceAbbreviationError, setServiceAbbreviationError] = useState('')
  const [excludedStateCodes, setExcludedStateCodes] = useState<string[]>([])
  const [imageUrl, setImageUrl] = useState('')
  const [visitTypeToDelete, setVisitTypeToDelete] = useState<string | null>(null)
  // Set by the user-facing edit handlers below (never by the programmatic edit-enter hydration
  // effects, which call the raw setters). Drives the unsaved-changes guard while in edit mode.
  const [isServiceEdited, setIsServiceEdited] = useState(false)

  const dispatch = useAppDispatch()

  const service = id !== null ? savedData[id] : undefined
  const normalizedIntakeForms = useMemo(() => normalizeIntakeForms(intakeForms), [intakeForms])

  // Set the browser tab title to the service name; reset to the default on unmount.
  useEffect(() => {
    if (serviceName) {
      document.title = serviceName
    }
    return () => {
      document.title = QUICK_MD_TITLE_TEXT
    }
  }, [serviceName])

  const closeDeleteConfirmationPopUp = useCallback(() => {
    setShowDeleteConfirmation(false)
  }, [])

  const openDeleteConfirmationPopUp = useCallback(() => {
    setShowDeleteConfirmation(true)
  }, [])

  const onResetServiceInfo = useCallback(() => {
    dispatch(changeTabs('Service types'))
    setIsEditMode(false)
    imageFileRef.current = null
    setImageUrl(serverImageUrlRef.current)
  }, [dispatch])

  // Clear the edited flag whenever we leave edit mode (cancel, save success, delete).
  useEffect(() => {
    if (!isEditMode) setIsServiceEdited(false)
  }, [isEditMode])

  // Guard the two exit paths (Cancel + nav-bar) once the user has edited while in edit mode.
  const {
    isPopupOpen: isDiscardPopupOpen,
    handleCancel: onCancelEdit,
    handleDiscard: onDiscardChanges,
    handleKeepEditing: onKeepEditing,
  } = useDiscardChangesGuard({ isDirty: isEditMode && isServiceEdited, onConfirmLeave: onResetServiceInfo })

  const goToPreviousPage = useCallback(() => {
    onResetServiceInfo()
    void navigate(`/management${locationState?.search ?? ''}`)
  }, [onResetServiceInfo, navigate, locationState])

  const onDeleteServiceType = useCallback(() => {
    if (id === null) return
    deleteServiceType(id, {
      onSuccess: () => {
        closeDeleteConfirmationPopUp()
        goToPreviousPage()
      },
      onError: (message) => {
        setError(message)
      },
    })
  }, [id, deleteServiceType, closeDeleteConfirmationPopUp, goToPreviousPage])

  useEffect(() => {
    const container = document.getElementById('container')
    if (container?.parentElement) {
      container.parentElement.style.overflowX = 'hidden'
    }

    const intervalId = setInterval(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)
    return () => clearInterval(intervalId)
  }, [])
  useEffect(() => {
    dispatch(changeTabs('Service types'))
  }, [dispatch])

  useEffect(() => {
    if (isEditMode && service) {
      setServiceTypes(
        (service.VisitTypes ?? []).map((t) => ({
          ...t,
          VisitTypeId: t.VisitTypeId ?? null,
          Name: t.Name ?? null,
          Description: t.Description ?? null,
          Duration: t.Duration ?? 0,
          VisibilityType: t.VisibilityType ?? null,
          IsMedicationAssistedTreatment: t.IsMedicationAssistedTreatment ?? false,
          IsPostBookingReminderRequired: t.IsPostBookingReminderRequired ?? false,
          IsPhoneVisitCommunicationTypeAllowed: t.IsPhoneVisitCommunicationTypeAllowed ?? false,
          IsInPerson: t.IsInPerson ?? false,
          IsAsynchronous: t.IsAsynchronous ?? false,
          IsNewPatientSpacingEnabled: t.IsNewPatientSpacingEnabled ?? false,
          Cost: formatMoney(t.Cost),
          CancellationFee: formatMoney(t.CancellationFee),
          ConsentForms: t.ConsentForms ?? [],
          IntakeFormId: t.IntakeForm?.IntakeFormId ?? null,
          errors: { ...emptyVisitTypeErrors },
        })),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot service into editable form state only when edit mode toggles; `service` intentionally omitted so a background refetch doesn't clobber in-progress edits
  }, [isEditMode])

  const onCreateNewServiceType = useCallback(() => {
    setIsServiceEdited(true)
    setServiceTypes([
      ...serviceTypes,
      {
        VisitTypeId: crypto.randomUUID(),
        isNew: true,
        Name: '',
        Description: '',
        Duration: 15,
        Cost: '',
        CancellationFee: '',
        Tacks: [],
        IntakeFormId: '',
        ConsentForms: [],
        VisibilityType: 'all',
        IsMedicationAssistedTreatment: false,
        IsPostBookingReminderRequired: false,
        IsPhoneVisitCommunicationTypeAllowed: true,
        IsInPerson: false,
        IsAsynchronous: false,
        IsNewPatientSpacingEnabled: false,
        errors: { ...emptyVisitTypeErrors },
      },
    ])
  }, [serviceTypes])

  useEffect(() => {
    if (service && !isEditMode) {
      setServiceTypes(
        (service.VisitTypes ?? []).map((t) => ({
          ...t,
          VisitTypeId: t.VisitTypeId ?? null,
          Name: t.Name ?? null,
          Description: t.Description ?? null,
          Duration: t.Duration ?? 0,
          VisibilityType: t.VisibilityType ?? null,
          IsMedicationAssistedTreatment: t.IsMedicationAssistedTreatment ?? false,
          IsPostBookingReminderRequired: t.IsPostBookingReminderRequired ?? false,
          Cost: formatMoney(t.Cost),
          CancellationFee: formatMoney(t.CancellationFee),
          ConsentForms: t.ConsentForms ?? [],
          IntakeFormId: t.IntakeForm?.IntakeFormId ?? null,
          IsPhoneVisitCommunicationTypeAllowed: t.IsPhoneVisitCommunicationTypeAllowed ?? false,
          IsInPerson: t.IsInPerson ?? false,
          IsAsynchronous: t.IsAsynchronous ?? false,
          IsNewPatientSpacingEnabled: t.IsNewPatientSpacingEnabled ?? false,
          errors: { ...emptyVisitTypeErrors },
        })),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync read-only visit types when savedData/isEditMode change; `service`/`id` omitted since `service` is derived from savedData[id]
  }, [savedData, isEditMode])

  useEffect(() => {
    setError('')
  }, [])

  useEffect(() => {
    if (serviceErrorMessage) {
      setError(serviceErrorMessage)
    }
  }, [serviceErrorMessage])

  const onCheckServiceTypesTables = useCallback(() => {
    const localErrors: LocalErrors[] = []

    // Collect all visibility types
    const visibilityTypes = serviceTypes.map((t) => t.VisibilityType)

    // Check for invalid combination
    const hasInvalidCombination = checkInvalidCombination(visibilityTypes)

    // Check if any visibility type is 'all', 'new', or 'none'
    const isSomeVisibilityTypeAllOrNew = visibilityTypes.some(checkIsVisibilityTypeAllOrNew)

    serviceTypes.forEach((t) => {
      const {
        CancellationFee,
        Cost,
        Duration,
        Name,
        VisitTypeId,
        IntakeFormId,
        IntakeForm,
        Description,
        ConsentForms,
        errors,
      } = t
      const errorsObj: LocalErrors = {
        id: '',
        errors: {
          visitLengthError: '',
          visitLengthLocalError: '',
          coastUSDError: '',
          coastUSDLocalError: '',
          cancellationFeeError: '',
          cancellationFeeLocalError: '',
          visitNameError: '',
          visitNameLocalError: '',
          intakeFormError: '',
          intakeFormLocalError: '',
          visitDescriptionError: '',
          visitDescriptionLocalError: '',
          consentFormError: '',
          consentFormLocalError: '',
          visibilityTypeError: '',
        },
      }
      const firstConsentForm: ConsentFormEntry | undefined = ConsentForms[0]
      const hasActiveForm =
        firstConsentForm && hasOwnProperty(firstConsentForm, 'isActive') ?
          ConsentForms.some((consentForm) => consentForm.isActive)
        : !!ConsentForms.length

      errorsObj.id = VisitTypeId

      if (
        Name?.trim() === '' ||
        (Name?.trim().length ?? 0) > 70 ||
        !Description ||
        Description?.trim() === '' ||
        (Description?.trim().length ?? 0) > 500 ||
        Cost === '' ||
        !hasActiveForm ||
        CancellationFee === '' ||
        Number(Cost) > MAX_VISIT_PRICE ||
        Number(CancellationFee) > MAX_VISIT_PRICE ||
        errors.visitNameLocalError ||
        Number(CancellationFee) > Number(Cost) ||
        !(IntakeFormId?.length || IntakeForm?.Name?.length)
      ) {
        if (
          Name?.trim() === '' &&
          (!Description || Description?.trim() === '') &&
          Cost === '' &&
          CancellationFee === '' &&
          !hasActiveForm &&
          !(IntakeFormId?.length || IntakeForm?.Name?.length)
        ) {
          errorsObj.errors.visitNameError = true
          errorsObj.errors.visitNameLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Visit name',
          )
          errorsObj.errors.visitDescriptionError = true
          errorsObj.errors.visitDescriptionLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Description',
          )
          errorsObj.errors.coastUSDLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Cost (USD)',
          )
          errorsObj.errors.cancellationFeeLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Cancellation fee',
          )
          errorsObj.errors.consentFormLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Consent forms',
          )
          errorsObj.errors.intakeFormError = true
          errorsObj.errors.intakeFormLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Intake form',
          )
          localErrors.push(errorsObj)
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          return
        }
        if (Name?.trim() === '') {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.visitNameError = true
          errorsObj.errors.visitNameLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Visit name',
          )
        }
        if ((Name?.trim().length ?? 0) > 70) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.visitNameError = true
          errorsObj.errors.visitNameLocalError = getUserFriendlyErrorMessage(
            errorCodes.MAX_CHARACTER_LIMIT,
            null,
            'Visit name',
          )
        }
        if (Description?.trim() === '' || !Description) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.visitDescriptionError = true
          errorsObj.errors.visitDescriptionLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Description',
          )
        }
        if ((Description?.trim().length ?? 0) > 500) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.visitDescriptionError = true
          errorsObj.errors.visitDescriptionLocalError = getUserFriendlyErrorMessage(
            errorCodes.MAX_CHARACTER_LIMIT,
            null,
            'Description',
          )
        }
        if (Duration === undefined || Duration === null || Number.isNaN(Duration)) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.visitLengthError = true
          errorsObj.errors.visitLengthLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Visit length',
          )
        }
        if (Cost === '') {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.coastUSDError = true
          errorsObj.errors.coastUSDLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Cost (USD)',
          )
        }
        if (CancellationFee === '') {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.cancellationFeeError = true
          errorsObj.errors.cancellationFeeLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Cancellation fee (USD)',
          )
        }
        if (Number(Cost) > MAX_VISIT_PRICE) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.coastUSDError = true
          errorsObj.errors.coastUSDLocalError = getUserFriendlyErrorMessage(
            errorCodes.BIG_VALUE,
            null,
            String(MAX_VISIT_PRICE),
          )
        }
        if (Number(CancellationFee) > MAX_VISIT_PRICE) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.cancellationFeeError = true
          errorsObj.errors.cancellationFeeLocalError = getUserFriendlyErrorMessage(
            errorCodes.BIG_VALUE,
            null,
            String(MAX_VISIT_PRICE),
          )
        }
        if (Number(CancellationFee) > Number(Cost)) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.cancellationFeeError = true
          errorsObj.errors.cancellationFeeLocalError = getUserFriendlyErrorMessage(
            errorCodes.BIG_VALUE,
            null,
            String(Cost || 'Cost (USD) field value'),
          )
        }
        if (!hasActiveForm) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.consentFormError = true
          errorsObj.errors.consentFormLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Consent forms',
          )
        }
        if (!(IntakeFormId?.length || IntakeForm?.Name?.length)) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          errorsObj.errors.intakeFormError = true
          errorsObj.errors.intakeFormLocalError = getUserFriendlyErrorMessage(
            errorCodes.FIELD_REQUIRED,
            null,
            'Intake form',
          )
        }
      }
      localErrors.push(errorsObj)
    })
    if (!isSomeVisibilityTypeAllOrNew || hasInvalidCombination) {
      setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
    }

    if (serviceTypes.length > 1) {
      const names = serviceTypes.map((t) => (t.Name ?? '').toLowerCase())
      const dublicates: string[] = toFindDuplicates(names)
      if (dublicates.length > 0) {
        setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
        const wrongNameFields = serviceTypes.map((type) =>
          dublicates
            .map((t) => (t.toLowerCase() === (type.Name ?? '').toLowerCase() ? type : ''))
            .filter((t) => t !== ''),
        )
        const correctArray = wrongNameFields.map((t) => t[0] ?? '').filter((t): t is ViewServiceVisitType => t !== '')
        for (let i = 0; i < localErrors.length; i += 1) {
          for (let j = 0; j < correctArray.length; j += 1) {
            const currentLocalError = localErrors[i]
            const currentCorrect = correctArray[j]
            if (currentLocalError && currentCorrect && currentLocalError.id === currentCorrect.VisitTypeId) {
              localErrors[i] = {
                ...currentLocalError,
                errors: {
                  ...currentLocalError.errors,
                  visitNameLocalError: getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Visit name'),
                  visitNameError: true,
                },
              }
            }
          }
        }
      }
    }

    let errorOccurred = 0
    localErrors.forEach((t) => {
      if (Object.values(t.errors).every((x) => x !== false && x !== '')) {
        errorOccurred += 1
      }
    })

    let isHasError = false
    localErrors.forEach((t) => {
      if (Object.values(t.errors).find((x) => x)) {
        isHasError = true
      }
    })

    if (errorOccurred > 0 || isHasError || !isSomeVisibilityTypeAllOrNew || hasInvalidCombination) {
      localStorage.setItem('viewServiceError', JSON.stringify(true))
    } else {
      localStorage.removeItem('viewServiceError')
    }

    setServiceTypes(
      serviceTypes.map((serviceType) => {
        const correctData = localErrors.find((t) => t.id === serviceType.VisitTypeId)
        let visibilityTypeError = ''

        if (hasInvalidCombination && serviceType.VisibilityType === 'established') {
          visibilityTypeError = getUserFriendlyErrorMessage(errorCodes.SET_ONE_VISIBILITY_AS_ALL_OR_NEW)
        } else if (!isSomeVisibilityTypeAllOrNew) {
          visibilityTypeError = getUserFriendlyErrorMessage(errorCodes.SET_ONE_VISIBILITY_AS_ALL_OR_NEW)
        }

        return {
          ...serviceType,
          errors: {
            ...(correctData ? correctData.errors : serviceType.errors),
            visibilityTypeError,
          },
        }
      }),
    )
  }, [serviceTypes])

  useEffect(() => {
    if (error === getUserFriendlyErrorMessage(errorCodes.ITEM_DOES_NOT_EXIST, null, 'Service type')) {
      void navigate(`/management${locationState?.search ?? ''}`)
    }

    if (
      error === getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS) ||
      error === getUserFriendlyErrorMessage(errorCodes.NO_CONNECTION) ||
      error === getUserFriendlyErrorMessage(errorCodes.DEFAULT) ||
      error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS)
    ) {
      setTimeout(() => {
        setError('')
      }, 3500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react only to `error` changes; navigate/locationState intentionally omitted to avoid re-navigating on unrelated location updates
  }, [error])

  useEffect(() => {
    if (service && !isEditMode) {
      setDescription(service.Description ?? '')
      setServiceName(service.Name ?? '')
      setServiceAbbreviation(service.Abbreviation ?? '')
      const serverUrl = readStringProp(service, 'ImageUrl')
      serverImageUrlRef.current = serverUrl
      if (!imageFileRef.current) {
        setImageUrl(serverUrl)
      }
      const excluded = readExcludedStateCodes(service)
      if (excluded !== undefined) {
        setExcludedStateCodes(excluded)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-hydrate service fields when savedData/isEditMode/id change; `service` omitted since it is derived from savedData[id]
  }, [savedData, isEditMode, id])

  const onSetServiceName = useCallback(
    (value: string) => {
      setIsServiceEdited(true)
      setServiceName(value)
      setError('')
      if (value.length < 70) {
        setServiceNameError('')
      }
      setIsDescriptionError(false)
      if (error) setError('')
    },
    [error],
  )
  const onSetServiceAbbreviation = useCallback(
    (value: string) => {
      setIsServiceEdited(true)
      setServiceAbbreviation(value)
      if (value.length < maxServiceAbbreviationLength) {
        setServiceAbbreviationError('')
      }
      if (error) setError('')
    },
    [error],
  )
  const onConfirmDeleteVisitType = useCallback(() => {
    setIsServiceEdited(true)
    setServiceTypes((prev) => prev.filter((t) => t.VisitTypeId !== visitTypeToDelete))
    setVisitTypeToDelete(null)
  }, [visitTypeToDelete])

  const onImageChange = useCallback((file: File) => {
    setIsServiceEdited(true)
    imageFileRef.current = file
    setImageUrl(URL.createObjectURL(file))
  }, [])

  const onDescriptionChange = useCallback(
    (value: string) => {
      setIsServiceEdited(true)
      setDescription(value)
      if (descriptionLocalError) setDescriptionLocalError('')
      if (isDescriptionError) setIsDescriptionError(false)
      if (error) setError('')
    },
    [descriptionLocalError, isDescriptionError, error],
  )

  const onSaveServices = useCallback(
    (newServiceObj: RawServicePayload) => {
      if (id === null) return
      // newServiceObj carries the raw working values (used as-is for the deep-equal check upstream).
      // saveService's contract requires non-null Name/Description/IntakeFormId/VisibilityType; this is
      // only reached after validation has confirmed those fields are filled, so the coalescing below
      // is a no-op on every reachable value and preserves the original payload.
      const body: ServiceTypeAdminApiInContract = {
        Name: newServiceObj.Name,
        Description: newServiceObj.Description,
        Abbreviation: newServiceObj.Abbreviation,
        VisitTypes: newServiceObj.VisitTypes.map((vt) => ({
          VisitTypeId: vt.VisitTypeId,
          IntakeFormId: vt.IntakeFormId ?? '',
          Name: vt.Name ?? '',
          Description: vt.Description ?? '',
          VisibilityType: vt.VisibilityType ?? '',
          Duration: vt.Duration,
          Cost: vt.Cost,
          CancellationFee: vt.CancellationFee,
          IsMedicationAssistedTreatment: vt.IsMedicationAssistedTreatment,
          IsPostBookingReminderRequired: vt.IsPostBookingReminderRequired,
          IsPhoneVisitCommunicationTypeAllowed: vt.IsPhoneVisitCommunicationTypeAllowed,
          IsInPerson: vt.IsInPerson,
          IsAsynchronous: vt.IsAsynchronous,
          IsNewPatientSpacingEnabled: vt.IsNewPatientSpacingEnabled,
          ConsentForms: vt.ConsentForms.map((cf) => ({ ConsentFormId: cf.ConsentFormId ?? null })),
        })),
      }
      saveService(body, id, {
        onSuccess: () => {
          setSaveSuccess(true)
        },
        onError: (message) => {
          setError(message)
        },
      })
    },
    [saveService, id],
  )

  const onSaveService = useCallback(() => {
    if (!service) return
    // TODO: when backend adds /api/v1/servicetypes/images, upload imageFile here
    // and pass the resulting ImageUrl in newServiceObj before dispatching saveService.
    const newServiceObj: RawServicePayload = {
      Name: serviceName.trim(),
      Description: description.trim(),
      Abbreviation: serviceAbbreviation.trim(),
      ServiceTypeId: id ?? undefined,
      ExcludedStateCodes: excludedStateCodes,
      VisitTypes: serviceTypes.map((vt) => {
        const {
          CancellationFee,
          Cost,
          Duration,
          Name,
          VisitTypeId,
          isNew,
          IntakeFormId,
          IntakeForm,
          AdditionalConsentForms,
          ConsentForms,
          IsPostBookingReminderRequired,
          IsMedicationAssistedTreatment,
          Description,
          VisibilityType,
          IsPhoneVisitCommunicationTypeAllowed,
          IsInPerson,
          IsAsynchronous,
          IsNewPatientSpacingEnabled,
        } = vt
        let consentFormsList: Array<{ ConsentFormId: string | null | undefined }>
        if (AdditionalConsentForms) {
          consentFormsList = AdditionalConsentForms.filter((consentForm) => consentForm.isActive).map((t) => ({
            ConsentFormId: t.id,
          }))
        } else if (hasOwnProperty(ConsentForms[0], 'isActive')) {
          consentFormsList = ConsentForms.filter((consentForm) => consentForm.isActive).map((consentForm) => ({
            ConsentFormId: consentForm.id,
          }))
        } else {
          consentFormsList = ConsentForms.map((consentForm) => ({
            ConsentFormId: consentForm.ConsentFormId,
          }))
        }

        return {
          IntakeFormId: IntakeFormId ?? IntakeForm?.IntakeFormId,
          ConsentForms: consentFormsList,
          VisitTypeId: isNew ? null : VisitTypeId,
          Name,
          IsMedicationAssistedTreatment,
          IsPostBookingReminderRequired,
          IsNewPatientSpacingEnabled,
          Duration: Number(Duration),
          Description,
          Cost: Number(formatMoney(Cost)),
          CancellationFee: Number(formatMoney(CancellationFee)),
          VisibilityType,
          IsPhoneVisitCommunicationTypeAllowed,
          IsInPerson,
          IsAsynchronous,
        }
      }),
    }
    const initialSavedData = {
      ...service,
      VisitTypes: (service.VisitTypes ?? []).map(
        ({ CreateDateTime: _CreateDateTime, IntakeForm, ConsentForms, ...rest }) => ({
          ...rest,
          ConsentForms: ConsentForms?.map((consentForm) => ({
            ConsentFormId: consentForm.ConsentFormId,
          })),
          IntakeFormId: IntakeForm?.IntakeFormId,
        }),
      ),
    }

    if (isObjectDeeplyEqual(initialSavedData, newServiceObj)) {
      onResetServiceInfo()
    } else {
      onCheckServiceTypesTables()
      if (
        serviceName.trim() === '' ||
        serviceAbbreviation.trim() === '' ||
        description.replaceAll('\n', '').trim() === '' ||
        description.length > maxDescriptionLength ||
        serviceName.length > maxServiceNameLength ||
        serviceAbbreviation.length > maxServiceAbbreviationLength
      ) {
        if (
          serviceName.trim() === '' &&
          description.replaceAll('\n', '').trim() === '' &&
          serviceAbbreviation.trim() === ''
        ) {
          setIsDescriptionError(true)
          setServiceNameError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Service name'))
          setDescriptionLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Description'))
          setServiceAbbreviationError(
            getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Service abbreviation'),
          )
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          return
        }
        if (serviceName.trim() === '') {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          setServiceNameError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Service name'))
        }
        if (serviceName.length > maxServiceNameLength) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          setServiceNameError(getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT, null, 'Service name'))
        }
        if (serviceAbbreviation.trim() === '') {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          setServiceAbbreviationError(
            getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Service abbreviation'),
          )
        }
        if (serviceAbbreviation.length > maxServiceAbbreviationLength) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          setServiceAbbreviationError(
            getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT, null, 'Service abbreviation'),
          )
        }
        if (description.length > maxDescriptionLength) {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          setDescriptionLocalError(getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT, null, 'Description'))
        }
        if (description.replaceAll('\n', '').trim() === '') {
          setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS, null, 'Description'))
          setDescriptionLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Description'))
          setIsDescriptionError(true)
        }
        localStorage.setItem('viewServiceError', JSON.stringify(true))
      }

      const erroringDetector = localStorage.getItem('viewServiceError')

      if (!erroringDetector) {
        onSaveServices(newServiceObj)
      }
    }
  }, [
    description,
    excludedStateCodes,
    id,
    onCheckServiceTypesTables,
    onResetServiceInfo,
    onSaveServices,
    service,
    serviceAbbreviation,
    serviceName,
    serviceTypes,
  ])

  useEffect(() => {
    if (saveSuccess) {
      setIsEditMode(false)
      dispatch(changeTabs('Service types'))
      setSaveSuccess(false)
    }
  }, [saveSuccess, dispatch])

  const onEditService = useCallback(() => {
    setIsEditMode(true)
  }, [])

  useEffect(() => {
    if (error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Service type')) {
      setServiceNameError(getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Service type'))
      setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
    }
  }, [error])

  // error-state surfaced via toast (replaces the inline ErrorAlert). Same trigger conditions as the
  // old inline alert: NO_CONNECTION / FILL_ALL_FIELDS / DEFAULT / NAME_ALREADY_EXISTS.
  useEffect(() => {
    if (
      error === getUserFriendlyErrorMessage(errorCodes.NO_CONNECTION) ||
      error === getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS) ||
      error === getUserFriendlyErrorMessage(errorCodes.DEFAULT) ||
      error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS)
    ) {
      showToast({ type: 'error', message: error, position: 'bottom-right' })
    }
  }, [error])

  if (error === '10 - Requested Service type does not exist. Please refresh your screen.') {
    return (
      <Navigate
        to={{
          pathname: `/NotFound404`,
        }}
      />
    )
  }

  const setServiceTypesForChild = (types: VisitTypeFormState[]) => {
    setIsServiceEdited(true)
    setServiceTypes(types.map((t) => ({ ...t, ConsentForms: t.ConsentForms ?? [] })))
  }

  const isActive = service ? readIsActive(service) : false

  const crumbs: Crumb[] =
    isEditMode ?
      [
        {
          label: 'Management',
          onClick: () => {
            void navigate(`/management${locationState?.search ?? ''}`)
          },
        },
        {
          label: 'Service types',
          onClick: () => {
            void navigate('/management?activeTab=Service types')
          },
        },
        { label: serviceName, onClick: onResetServiceInfo },
        { label: 'Edit' },
      ]
    : [
        {
          label: 'Management',
          onClick: () => {
            void navigate(`/management${locationState?.search ?? ''}`)
          },
        },
        {
          label: 'Service types',
          onClick: () => {
            void navigate('/management?activeTab=Service types')
          },
        },
        { label: serviceName },
      ]

  // Full-page loader. Rendered instead of the page shell (not alongside it) — an in-flow
  // LoadingSection is flex-1 and shared height with EditPageShell, pushing the breadcrumb /
  // Delete-Edit bar / "Service information" section to the bottom of the screen behind the
  // spinner. Form state lives in this component, so replacing the shell preserves it.
  if (loading) {
    return (
      <div className='flex flex-1 flex-col'>
        <LoadingSection />
      </div>
    )
  }

  return (
    <>
      <DeleteServiceTypePopup
        isOpen={showDeleteConfirmation}
        onClose={closeDeleteConfirmationPopUp}
        onConfirm={onDeleteServiceType}
      />
      <DiscardChangesPopup
        isOpen={isDiscardPopupOpen}
        entityName={serviceName}
        onDiscard={onDiscardChanges}
        onKeepEditing={onKeepEditing}
      />
      <EditPageShell
        breadcrumb={<Breadcrumb crumbs={crumbs} />}
        title={
          <>
            <h1 className='m-0 text-size-6 font-semibold leading-8 text-text-dark'>
              {isEditMode ? `Edit ${serviceName}` : serviceName}
            </h1>
            {!isEditMode && !!service && (
              <Badge variant={isActive ? 'active' : 'idle'}>{isActive ? 'Active' : 'Inactive'}</Badge>
            )}
          </>
        }
        titleActions={
          !isEditMode ?
            <Button priority='primary' size='m' onClick={onEditService}>
              <EditIcon iconSize='m' />
              Edit
            </Button>
          : undefined
        }
        constrainMinWidth>
        <ServiceInfoSection
          isEditMode={isEditMode}
          serviceName={serviceName}
          serviceNameError={serviceNameError}
          onSetServiceName={onSetServiceName}
          serviceAbbreviation={serviceAbbreviation}
          serviceAbbreviationError={serviceAbbreviationError}
          onSetServiceAbbreviation={onSetServiceAbbreviation}
          description={description}
          descriptionLocalError={descriptionLocalError}
          isDescriptionError={isDescriptionError}
          onDescriptionChange={onDescriptionChange}
          imageUrl={imageUrl}
          onImageChange={onImageChange}
          excludedStateCodes={excludedStateCodes}
          onExcludedStateCodesChange={(codes) => {
            setIsServiceEdited(true)
            setExcludedStateCodes(codes)
          }}
        />
        <DeleteVisitTypePopup
          isOpen={visitTypeToDelete !== null}
          onClose={() => setVisitTypeToDelete(null)}
          onConfirm={onConfirmDeleteVisitType}
        />
        <VisitTypesSection
          isEditMode={isEditMode}
          serviceTypes={serviceTypes}
          setServiceTypes={setServiceTypesForChild}
          intakeForms={normalizedIntakeForms}
          additionalConsentFormsOptions={additionalConsentFormsOptions}
          onRequestDeleteVisitType={setVisitTypeToDelete}
        />
        {isEditMode ?
          <ViewServiceFooter
            onAddVisitType={onCreateNewServiceType}
            onDeleteService={openDeleteConfirmationPopUp}
            onCancel={onCancelEdit}
            onSave={onSaveService}
          />
        : null}
      </EditPageShell>
    </>
  )
}
